import Cookies from 'js-cookie';
import { parse } from 'tldts';
import type { Data, DataCookieValue } from './types';

const OPT_IN_TTL_DAYS = 180;
const NOTICE_ONLY_TTL_DAYS = 365;

type CookieOptions = {
  domain?: string;
  expires: Date;
  path: string;
  sameSite: 'Lax';
  secure: boolean;
};

export function resolveBrowserCookieOptions() {
  if (typeof window === 'undefined' || !window.location) {
    return { domain: undefined, secure: true };
  }

  const { hostname, protocol } = window.location;
  const { domain, isIcann, isPrivate } = parse(hostname, { allowPrivateDomains: true });

  return {
    domain: domain && (isIcann || isPrivate) ? `.${domain}` : undefined,
    secure: protocol === 'https:',
  };
}

export class ConsentCookie {
  private static readonly NAME = 'converge_cookie_consent';

  read(): DataCookieValue | null {
    const raw = Cookies.get(ConsentCookie.NAME);
    if (!raw) return null;
    return this.parse(raw);
  }

  write(data: Data): void {
    const payload: DataCookieValue = { v: 1, data };
    Cookies.set(
      ConsentCookie.NAME,
      JSON.stringify(payload),
      this.toCookieOptions(this.computeExpires(data)),
    );
  }

  remove(): void {
    Cookies.remove(ConsentCookie.NAME, this.toCookieOptions(new Date(0)));
  }

  private computeExpires(data: Data): Date {
    const days = data.mode === 'notice_only' ? NOTICE_ONLY_TTL_DAYS : OPT_IN_TTL_DAYS;
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    return expires;
  }

  private parse(raw: string): DataCookieValue | null {
    try {
      const parsed = JSON.parse(raw) as DataCookieValue;
      return parsed?.data ? parsed : null;
    } catch {
      return null;
    }
  }

  private toCookieOptions(expires: Date) {
    const { domain, secure } = resolveBrowserCookieOptions();

    return {
      path: '/',
      expires,
      secure,
      sameSite: 'Lax' as const,
      domain,
    } satisfies CookieOptions;
  }
}
